import type { AnimationPreset, BlockAnimation } from './types'

interface Props {
  value?: BlockAnimation
  onChange: (v: BlockAnimation | undefined) => void
}

const PRESETS: AnimationPreset[] = [
  'none', 'fade', 'fade-up', 'fade-down', 'fade-left', 'fade-right',
  'scale-in', 'slide-up', 'slide-down', 'zoom-in', 'blur-in', 'clip-wipe',
]

export function AnimationEditor({ value, onChange }: Props) {
  const v = value ?? { preset: 'none' as AnimationPreset }
  const set = (patch: Partial<BlockAnimation>) => {
    const next: BlockAnimation = { ...v, ...patch }
    if (next.preset === 'none') onChange(undefined)
    else onChange(next)
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Field label="Preset">
        <select value={v.preset} onChange={e => set({ preset: e.target.value as AnimationPreset })} style={inputStyle}>
          {PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </Field>
      {v.preset !== 'none' && (
        <>
          <Field label="Trigger">
            <select value={v.on ?? 'enter'} onChange={e => set({ on: e.target.value as BlockAnimation['on'] })} style={inputStyle}>
              <option value="enter">On enter (in viewport)</option>
              <option value="scroll">On scroll</option>
              <option value="load">On load</option>
              <option value="hover">On hover</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Field label="Delay (ms)">
              <input type="number" value={v.delay ?? 0} onChange={e => set({ delay: Number(e.target.value) })} style={inputStyle} />
            </Field>
            <Field label="Duration (ms)">
              <input type="number" value={v.duration ?? 600} onChange={e => set({ duration: Number(e.target.value) })} style={inputStyle} />
            </Field>
          </div>
          <Field label="Easing">
            <select value={v.easing ?? 'ease-out'} onChange={e => set({ easing: e.target.value as BlockAnimation['easing'] })} style={inputStyle}>
              <option value="ease">ease</option>
              <option value="ease-in">ease-in</option>
              <option value="ease-out">ease-out</option>
              <option value="ease-in-out">ease-in-out</option>
              <option value="linear">linear</option>
            </select>
          </Field>
          <Field label="Stagger children (ms)">
            <input
              type="number" min={0} max={1000} step={10}
              value={v.stagger ?? 0}
              onChange={e => set({ stagger: Number(e.target.value) || undefined })}
              style={inputStyle}
            />
            <span style={hintStyle}>
              0 = animate the block as one. Above 0, each direct child starts
              one step later — what makes a card grid or feature list read as
              designed rather than rendered.
            </span>
          </Field>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={v.once ?? true} onChange={e => set({ once: e.target.checked })} />
            Play once
          </label>
          <div style={hintStyle}>
            Motion is an enhancement: the block is fully visible without it, and
            it is skipped entirely for visitors who ask for reduced motion.
            Select the block on the canvas to replay and tune the timing.
          </div>
        </>
      )}
    </div>
  )
}

const hintStyle: React.CSSProperties = {
  fontSize: 13, lineHeight: 1.45, color: 'var(--color-text-secondary)',
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
